import {clone} from 'lodash-es';
import {cloneDeep} from 'lodash-es';
import {uniq} from 'lodash-es';
import {sortBy} from 'lodash-es';
import {intersection} from 'lodash-es';
import {IterableUtil} from '../collections/collection-utils';

type NotVoid = unknown;
type PropertyName = string | number | symbol;
type List<T> = ArrayLike<T>;
type Many<T> = T | ReadonlyArray<T>;
type PartialShallow<T> = {
  [P in keyof T]?: T[P] extends object ? object : T[P]
};
type IterateeShorthand<T> = PropertyName | [PropertyName, any] | PartialShallow<T>;
type ListIterator<T, TResult> = (value: T, index: number, collection: List<T>) => TResult;
type ListIteratee<T> = ListIterator<T, NotVoid> | IterateeShorthand<T>;
type ElementOf<T> = T extends Array<infer U> ? U : never;
type ObjectKey = ElementOf<ReturnType<typeof Object.keys>>;

export class ObjectUtils {

  /**
   * Creates a shallow clone of value.
   *
   * Note: This method is loosely based on the structured clone algorithm and supports cloning arrays,
   * array buffers, booleans, date objects, maps, numbers, Object objects, regexes, sets, strings, symbols,
   * and typed arrays. The own enumerable properties of arguments objects are cloned as plain objects. An empty
   * object is returned for uncloneable values such as error objects, functions, DOM nodes, and WeakMaps.
   *
   * @param value The value to clone.
   * @return Returns the cloned value.
   */
  public static clone<T>(value: T): T {
    return clone(value);
  }

  /**
   * This method is like _.clone except that it recursively clones value.
   *
   * @param value The value to recursively clone.
   * @return Returns the deep cloned value.
   */
  public static cloneDeep<T>(value: T): T {
    return cloneDeep(value);
  }

  /**
   * Creates a duplicate-free version of an array, using
   * [`SameValueZero`](http://ecma-international.org/ecma-262/6.0/#sec-samevaluezero)
   * for equality comparisons, in which only the first occurrence of each element
   * is kept.
   *
   * @param array The array to inspect.
   * @returns Returns the new duplicate free array.
   */
  public static uniq<T>(array: List<T> | null | undefined): T[] {
    return uniq(array);
  }

  /**
   * Creates an array of elements, sorted in ascending order by the results of
   * running each element in a collection through each iteratee. This method
   * performs a stable sort, that is, it preserves the original sort order of
   * equal elements. The iteratees are invoked with one argument: (value).
   *
   * @param collection The collection to iterate over.
   * @param [iteratees=[_.identity]] The iteratees to sort by, specified individually or in arrays.
   * @returns Returns the new sorted array.
   */
  public static sortBy<T>(collection: List<T> | null | undefined, ...iteratees: Array<Many<ListIteratee<T>>>): T[] {
    return sortBy(collection, ...iteratees);
  }

  /**
   * Creates an array of unique values that are included in all of the provided arrays using SameValueZero for
   * equality comparisons.
   *
   * @param arrays The arrays to inspect.
   * @return Returns the new array of shared values.
   */
  public static intersection<T>(...arrays: Array<List<T> | null | undefined>): T[] {
    return intersection(...arrays);
  }

  public static deleteKeys(obj: object, keys: Iterable<ObjectKey>): object {
    for (const key of keys) {
      if (key in obj) {
        delete obj[key];
      }
    }
    return obj;
  }

  public static arraysEqual(a: ArrayLike<any>, b: ArrayLike<any>): boolean {
    if (a === b || (a == null && b == null)) return true;
    if (a == null || b == null) return false;
    if (a.length !== b.length) return false;

    for (var i = 0; i < a.length; ++i) {
      if (a[i] !== b[i]) return false;
    }
    return true;
  }

  public static findValue<T>(obj: T, value: any): T[keyof T] | null {
    // When find() does not find anything it returns undefined. Replace undefined with null
    return obj != null ? (Object.values(obj).find(t => t === value) ?? null) : null;
  }

  public static getEnumValueFromString<T>(enumType: T, str: string): T[keyof T] | null {
    return ObjectUtils.findValue(enumType, str);
  }

  public static isEmpty(obj: object): boolean {
    return obj == null || Object.keys(obj).length === 0;
  }

  public static cloneClassInstance<T, T1>(obj: T, ...newPropertyValues: Partial<T1>[]): T & T1 {
    return Object.assign(Object.create(Object.getPrototypeOf(obj)), obj, ...newPropertyValues);
  }

  public static mapObjectOrArray<T, R>(obj: T | T[], mapFn: (a: T) => R): R | R[] {
    return Array.isArray(obj) ? obj.map(item => mapFn(item)) : mapFn(obj);
  }

  public static keepNonNullValues<T extends object>(value: T): Partial<T> {
    return Object.entries(value).reduce((partialObject, [key, value]) => {
      if (value != null) {
        partialObject[key] = value;
      }
      return partialObject;
    }, <Partial<T>>{})
  }

  /** Exactly just `Object.entries` but types the return. */
  public static entries<T extends object>(objectValue: T): [keyof T, T[keyof T]][] {
    return <[keyof T, T[keyof T]][]>Object.entries(objectValue);
  }

  /** Creates another object with same keys as `objectValue` but with values mapped using `mapValueFn` */
  public static mapValues<T extends object, U>(objectValue: T, mapValueFn: (value: T[keyof T]) => U): {[key in keyof T]: U} {
    return IterableUtil.reduce(
      ObjectUtils.entries(objectValue),
      (mappedObject, [key, value]) => {
        mappedObject[key] = mapValueFn(value);
        return mappedObject;
      },
      <{[key in keyof T]: U}>{}
    )
  }
}
